import type { Stance } from "./types";

// The stances (2026-10-08: "make our pvp stances white, yellow or red with
// the expected meanings"): Tibia's hands, less the parties and guilds this
// world does not have. The server has the rule (internal/game/stance.go);
// this is only what the control and the panel say about it.

/** In the order the control steps through them, the gentlest first. */
export const STANCES: readonly Stance[] = ["white", "yellow", "red"];

/** The stance a press of the control asks for: the next one round. */
export function nextStance(st: Stance): Stance {
  return STANCES[(STANCES.indexOf(st) + 1) % STANCES.length];
}

/** The drawing for a stance: an open hand, a hand, a fist. */
export function stanceGlyph(st: Stance): string {
  return `stance${st}`;
}

/** The word on the control and on the Statuses line. */
export function stanceWord(st: Stance): string {
  return `PvP: ${st}`;
}

/** What a stance lets you attack, in a sentence of its own. */
export function stanceMeaning(st: Stance): string {
  switch (st) {
    case "red":
      return "Red fist: you may attack anybody, and clicking a player attacks them. Attacking somebody who is not protection zone locked and has not attacked you locks you out of the protection zones until the fight is over, and killing them puts a skull on you.";
    case "yellow":
      return "Yellow hand: you may attack whoever attacked you and anybody protection zone locked; clicking anybody else follows them. It never locks you or earns you a skull.";
  }
  return "White hand: you may attack only whoever attacked you; clicking anybody else follows them. It never locks you or earns you a skull.";
}

/**
 * The control's hover: what the stance is, what pressing it changes to, and
 * that spells keep to it too.
 */
export function stanceTitle(st: Stance): string {
  const next = nextStance(st);
  const spells =
    st === "red"
      ? "Chain lightning, fire nova and Istari light strike anybody in their way."
      : "Chain lightning, fire nova and Istari light keep to it: they refuse while anybody it forbids is in the way.";
  return `${stanceMeaning(st)} ${spells} Click for ${next}.`;
}

/**
 * Whether somebody at their level, locked or not, is too far below you to
 * attack whatever your stance: under half your level and not protection
 * zone locked (2026-10-09: "don't allow attacking players less than half
 * your level, unless they are skulled"; the lock is what that skull became
 * on 2026-10-24). The server's levelBars says the same; this is
 * only for saying why the menu's Attack is greyed.
 */
export function levelBars(mine: number, theirs: number | undefined, locked: boolean | undefined): boolean {
  return !locked && theirs !== undefined && 2 * theirs < mine;
}

/**
 * Why the player menu's Attack is greyed: they are under half your level
 * and are not protection zone locked, or they are not somebody the stance allows, and what
 * would let you.
 */
export function attackForbids(st: Stance, mine: number, theirs: number | undefined, locked: boolean | undefined): string {
  if (levelBars(mine, theirs, locked)) {
    return `They are level ${theirs}, less than half your level ${mine}, and are not protection zone locked. Nobody may attack somebody under half their level unless they are, whatever the stance`;
  }
  return stanceForbids(st);
}

/**
 * Why the player menu's Attack is greyed by the stance: they are not
 * somebody it allows, and what would let you.
 */
export function stanceForbids(st: Stance): string {
  const what = st === "yellow" ? "They are not protection zone locked and have not attacked you" : "They have not attacked you";
  return `${what}, and your stance is ${st}. Red attacks anybody — or hit back at anyone who hits you first, which any stance may`;
}
